import compact from 'lodash/compact'
import concat from 'lodash/concat'
import filter from 'lodash/filter'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, { FC, useCallback, useContext, useMemo, useState, VFC } from 'react'

import SVGCheck from '@Assets/SVG/Check'
import { ProductSlider } from '@Components/AddedToCartModal/ProductSlider'
import {
  CrossSellContainer,
  CrossSellHeader,
  CTAWrapper,
  ErrorMessages,
  FreeShippingNotice,
  Header,
  Icon,
  InlineIcon,
} from '@Components/AddedToCartModal/styledElements'
import { Button } from '@Components/Button'
import { SmallBuyButton } from '@Components/BuyButton/SmallBuyButton'
import { CrossSellWrapper, GoToCartLink } from '@Components/BuyButton/styledElements'
import { Title } from '@Components/CMS/Title'
import { SVGIcon } from '@Components/Icons'
import { Modal, SharedModalProps } from '@Components/Modal'
import { DebossModal } from '@Components/Personalization/Modals/Deboss'
import { EngravingModal } from '@Components/Personalization/Modals/Engraving'
import { PersonalisationModalTypes } from '@Components/Personalization/Modals/Shared'
import { UVPrintModal } from '@Components/Personalization/Modals/UVPrint'
import { PersonalizationCrossSells } from '@Components/Product/CrossSells/PersonalizationCrossSell'
import { ProductTile } from '@Components/Product/Tile'
import { AddToCartLegacyResponse, Product } from '@Generated/graphql'
import {
  generatePersonalizationData,
  getCrossSellsForProduct,
  getPersonalizationsFromProduct,
  getProductTilesFromRelatedProducts,
} from '@Globals/CMS/ProductHelpers'
import { MetadataContext } from '@Globals/Metadata'
import { formatPrice } from '@Globals/PriceHelpers'
import { useTranslation } from '@Globals/Translations'

interface Props extends SharedModalProps {
  data: AddToCartLegacyResponse & { variantIDToAdd?: string, withPersonalization?: boolean }
  product?: Product
}

const SliderWrapper: VFC<{ headline: string, items: JSX.Element[] }> = ({ headline, items }) => {
  if (!items || !items.length) {
    return null
  }

  return (
    <CrossSellContainer>
      <CrossSellHeader>{headline}</CrossSellHeader>
      <ProductSlider>
        {items}
      </ProductSlider>
    </CrossSellContainer>
  )
}

interface ModalState {
  currentModal: null | PersonalisationModalTypes
  hide: boolean
  isOpen: boolean
  selectedVariantID: string
  updatedProduct: boolean
}

const initModalState: ModalState = {
  isOpen: false,
  hide: false,
  currentModal: null,
  selectedVariantID: '',
  updatedProduct: false,
}

export const AddedToCartModal: FC<Props> = ({
  data,
  isOpen,
  onHide,
  product,
}) => {
  const { t, tDanger } = useTranslation('cart')
  const { locale } = useRouter()
  const [modalState, setModalState] = useState<ModalState>(initModalState)

  const { store: { id: storeID }, storeURL } = useContext(MetadataContext)
  const {
    addedProductIDsWithPersonalizationType,
    addedProductsAlreadyInCart,
    cartTotal,
    failedProducts,
    recommendations,
    shippingThreshold,
    userRecommendations,
    variantIDToAdd,
    withPersonalization = false,
  } = data
  const remainingUntilShippingIsFree = Math.max(0, (shippingThreshold?.value || 0) - (cartTotal?.value || 0))
  const shippingIsFree = remainingUntilShippingIsFree === 0

  const removeAddedProducts = useCallback(
    collection => filter(collection, item => (!data.addedProducts?.productIDs ? true : !data.addedProducts.productIDs.includes(item.id))),
    [data.addedProducts?.productIDs],
  )

  const availableCrossSells = useMemo(
    () => removeAddedProducts(getCrossSellsForProduct(product)),
    [product, removeAddedProducts],
  )
  const availableRecommendations = useMemo(
    () => removeAddedProducts(getProductTilesFromRelatedProducts(recommendations?.products || [])),
    [recommendations?.products, removeAddedProducts],
  )
  const personalizationData = product
    && compact(generatePersonalizationData(getPersonalizationsFromProduct(product), {
      addedProductsAlreadyInCart,
      addedProductIDsWithPersonalizationType,
      productID: product.id,
      addedFromCartModal: withPersonalization || modalState.updatedProduct,
    }))

  const crossSellsSliderItems = concat(map(personalizationData || [], item => (
    <CrossSellWrapper key={item.id}>
      <PersonalizationCrossSells
        key={`${item.id}-personalization`}
        personalization={item}
        setModalState={setModalState}
      />
    </CrossSellWrapper>
  )), map(getProductTilesFromRelatedProducts(availableCrossSells, true), item => (
    <CrossSellWrapper key={item.key}>
      <ProductTile {...item} />
      <SmallBuyButton isCrossSell product={item} sourceID="high_cross_sales" />
    </CrossSellWrapper>
  )))
  const availablePersonalRecommendations = useMemo(
    () => removeAddedProducts(getProductTilesFromRelatedProducts(userRecommendations?.products || [])),
    [removeAddedProducts, userRecommendations?.products],
  )

  const recommendationsSliderItems = map(availableRecommendations, item => (
    <CrossSellWrapper key={item.key}>
      <ProductTile {...item} />
      {!availableCrossSells.length && <SmallBuyButton isCrossSell product={item} sourceID="high_cross_sales" />}
    </CrossSellWrapper>
  ))

  const personalRecommendationsSliderItems = map(availablePersonalRecommendations, item => (
    <ProductTile {...item} />
  ))

  const modalPersonalizationOptions = {
    product: product as Product,
    isOpen,
    onHide: () => setModalState(prev => ({
      ...prev,
      currentModal: null,
      updatedProduct: true,
    })),
    setModalState,
    currentModal: modalState.currentModal,
    selectedVariantID: variantIDToAdd,
    updateProduct: true,
  }

  if (!modalState.currentModal) {
    return (
      <Modal isOpen={isOpen} maxWidth="740px" onHide={onHide}>
        <Header>
          <Icon>
            <SVGIcon
              color="primary"
              icon={SVGCheck}
            />
          </Icon>
          <Title title={t('ADDED_TO_CART')} type="h2" />
        </Header>

        <ErrorMessages>
          {map(failedProducts, (failure) => {
            if (failure.error === 'REQUESTED_QUANTITY_NOT_IN_STOCK') {
              return (
                <li key={failure.productID}>
                  <strong>
                    {failure.product?.name}
                  </strong>
                  {' '}
                  <span {...tDanger('PP_IS_UNFORTUNATELY_OUT_OF_STOCK_TEXT')} />
                </li>
              )
            }

            return null
          })}
        </ErrorMessages>

        <FreeShippingNotice>
          {shippingIsFree
            ? (
              <>
                <InlineIcon color="greyDarkLightened10" icon={SVGCheck} />
                {' '}
                {t('YOU_HAVE_UNLOCKED_FREE_SHIPPING_ON_YOUR_ORDER')}
              </>
            )
            : (
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    'ADD_X_MORE_TO_UNLOCK_FREE_SHIPPING',
                    {
                      arg1: formatPrice(storeID, locale || '', remainingUntilShippingIsFree / 100),
                    },
                  ),
                }}
              />
            )}
        </FreeShippingNotice>

        <SliderWrapper
          headline={t('OTHER_CUSTOMERS_ALSO_BOUGHT')}
          items={crossSellsSliderItems.length ? crossSellsSliderItems : recommendationsSliderItems}
        />

        <CTAWrapper>
          <Button
            color="dark"
            onClick={onHide}
            variant="outlined"
          >
            {t('PP_CONTINUE_SHOPPING_BUTTON_TEXT')}
          </Button>
          <GoToCartLink
            color="primary"
            newTab={false}
            text={t('GO_TO_CART')}
            url={storeURL('checkout/cart')}
          />
        </CTAWrapper>

        <SliderWrapper
          headline={t('RECOMMENDED_FOR_YOU')}
          items={crossSellsSliderItems.length ? recommendationsSliderItems : personalRecommendationsSliderItems}
        />

      </Modal>
    )
  }

  if (modalState.currentModal === 'engrave') {
    return <EngravingModal {...modalPersonalizationOptions} />
  }

  if (modalState.currentModal === 'deboss') {
    return <DebossModal {...modalPersonalizationOptions} />
  }

  if (modalState.currentModal === 'uv-print') {
    return <UVPrintModal {...modalPersonalizationOptions} />
  }

  return null
}

